/**
 * Converts imported project data (P6/MPP JSON) into PlanSummaryForAnalysis
 * for the schedule risk analysis endpoint.
 *
 * Usage:
 *   const result = convertToPlanSummary(projectData);
 *   // result.planSummary, result.taskIdMap, result.groupIdMap, etc.
 */

import type {
  ImportedProject, ImportedTask, ImportedCalendar, ImportedResource,
  TaskSummary, GroupSummary, ResourceSummary, LinkSummary,
  CriticalPathSummary, TaskAssignmentSummary, HolidaySummary, LinkLag,
  IdMap, ConvertToPlanSummaryResult,
  MeetingSummary,
} from './types';

interface FlatTask {
  task: ImportedTask;
  shortId: string;
  parentGroupShortId: string | null;
}

interface DescendantAggregate {
  taskCount: number;
  totalEffortHours: number;
  tasksCompletedCount: number;
  tasksInProgressCount: number;
  tasksNotStartedCount: number;
}

export function convertToPlanSummary(data: ImportedProject): ConvertToPlanSummaryResult {
  let taskIndex = 0;
  let groupIndex = 0;
  let milestoneIndex = 0;
  let resourceIndex = 0;

  const taskIdMap: IdMap = {};
  const groupIdMap: IdMap = {};
  const milestoneIdMap: IdMap = {};
  const resourceIdMap: IdMap = {};
  const taskRealIdMap: IdMap = {};
  const groupRealIdMap: IdMap = {};
  const milestoneRealIdMap: IdMap = {};
  const resourceRealIdMap: IdMap = {};

  // Internal ID → short ID lookups
  const uidToTaskShortId = new Map<string, string>();
  const uidToGroupShortId = new Map<string, string>();
  const uidToMsShortId = new Map<string, string>();
  const uidToResShortId = new Map<string, string>();

  // Resource UID → name lookup
  const resourceUidToName = new Map<number, string | undefined>();
  for (const r of data.resources || []) {
    if (r.uniqueID != null) resourceUidToName.set(r.uniqueID, r.name);
  }
  const importedCalendarByName = new Map<string, ImportedCalendar>();
  const importedCalendarById = new Map<string, ImportedCalendar>();
  const schedulingCalendarById = new Map<string, ImportedCalendar>();
  for (const calendar of data.calendars || []) {
    if (calendar && calendar.name) importedCalendarByName.set(calendar.name, calendar);
    if (calendar && calendar.uniqueID != null) importedCalendarById.set(String(calendar.uniqueID), calendar);
    if (calendar && calendar.id != null) schedulingCalendarById.set(String(calendar.id), calendar);
  }

  function shortId(prefix: string, index: number): string {
    return '@id:' + prefix + '_' + String(index + 1).padStart(3, '0');
  }

  function resolveResourceCalendar(resource: ImportedResource): ImportedCalendar | null {
    if (!resource) return null;
    if (resource.calendarName && importedCalendarByName.has(resource.calendarName)) {
      return importedCalendarByName.get(resource.calendarName)!;
    }
    if (resource.calendarUniqueID != null && importedCalendarById.has(String(resource.calendarUniqueID))) {
      return importedCalendarById.get(String(resource.calendarUniqueID))!;
    }
    if (resource.calendarId != null && schedulingCalendarById.has(String(resource.calendarId))) {
      return schedulingCalendarById.get(String(resource.calendarId))!;
    }
    return null;
  }

  function getCanonicalCalendarName(calendar: ImportedCalendar): string {
    const parentName = calendar && calendar.parentCalendarName;
    if (parentName && importedCalendarByName.has(parentName)) {
      const parent = importedCalendarByName.get(parentName)!;
      if (calendarMatchesParent(calendar, parent)) {
        return parent.name || calendar.name || 'Standard';
      }
    }
    return (calendar && calendar.name) || 'Standard';
  }

  function calendarMatchesParent(calendar: ImportedCalendar, parent: ImportedCalendar): boolean {
    if (!calendar || !parent) return false;
    const calendarHours = getCalendarHoursPerDay(calendar);
    const parentHours = getCalendarHoursPerDay(parent);
    if (!calendarHours || !parentHours || calendarHours.join(',') !== parentHours.join(',')) {
      return false;
    }

    const calendarExceptions = getCalendarHolidays(calendar);
    const parentExceptions = getCalendarHolidays(parent);
    if (calendarExceptions.length !== parentExceptions.length) return false;
    for (let i = 0; i < calendarExceptions.length; i++) {
      if (calendarExceptions[i].name !== parentExceptions[i].name) return false;
    }
    return true;
  }

  function getCalendarHoursPerDay(calendar: ImportedCalendar): number[] | undefined {
    if (!calendar) return undefined;

    if (Array.isArray(calendar.daySchedules)) {
      const dayOrder = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      return dayOrder.map(day => {
        const schedule = calendar.daySchedules!.find(ds => ds.day === day);
        if (!schedule || !Array.isArray(schedule.workingHours)) return 0;
        return Math.round(schedule.workingHours.reduce((sum, period) => {
          return sum + timeRangeHours(period.start, period.end);
        }, 0) * 100) / 100;
      });
    }

    if (calendar.minutesPerDay && typeof calendar.minutesPerDay === 'object') {
      const dayOrder = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      return dayOrder.map(day => {
        const minutes = Number((calendar.minutesPerDay as Record<string, number>)[day] || 0);
        return Math.round((minutes / 60) * 100) / 100;
      });
    }

    return undefined;
  }

  function getCalendarHolidays(calendar: ImportedCalendar): HolidaySummary[] {
    if (!calendar || !Array.isArray(calendar.exceptions)) return [];
    return calendar.exceptions.map(ex => ({
      name: ex.name || ex.fromDate || ex.date || 'Unnamed holiday',
    }));
  }

  function timeRangeHours(start: string, end: string): number {
    const startMinutes = timeStringToMinutes(start);
    const endMinutes = timeStringToMinutes(end);
    if (startMinutes == null || endMinutes == null || endMinutes < startMinutes) return 0;
    return (endMinutes - startMinutes) / 60;
  }

  function timeStringToMinutes(value: string): number | null {
    if (typeof value !== 'string') return null;
    const parts = value.split(':').map(Number);
    if (parts.length < 2 || parts.some(Number.isNaN)) return null;
    return (parts[0] * 60) + parts[1];
  }

  function normalizeMaxUnitsPercent(value: number | undefined | null): number | undefined {
    if (value == null || value as unknown === '') return undefined;
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return undefined;
    const percent = numeric <= 1 ? numeric * 100 : numeric;
    return Math.round(percent * 100) / 100;
  }

  // First pass: assign short IDs to all items
  const flatTasks: FlatTask[] = [];
  const flatMilestones: FlatTask[] = [];
  const flatGroups: FlatTask[] = [];

  function classifyTasks(tasks: ImportedTask[] | undefined, parentGroupShortId: string | null): void {
    if (!tasks) return;
    for (const t of tasks) {
      if (t.isSummary && t.children && t.children.length > 0) {
        // Summary task → group
        const sid = shortId('group', groupIndex++);
        const uid = String(t.uniqueID || t.id || t.name);
        uidToGroupShortId.set(uid, sid);
        groupIdMap[sid] = t.name || 'Unnamed Group';
        groupRealIdMap[sid] = uid;
        flatGroups.push({ task: t, shortId: sid, parentGroupShortId });
        classifyTasks(t.children, sid);
      } else if (t.isMilestone) {
        const sid = shortId('ms', milestoneIndex++);
        const uid = String(t.uniqueID || t.id || t.name);
        uidToMsShortId.set(uid, sid);
        milestoneIdMap[sid] = t.name || 'Unnamed Milestone';
        milestoneRealIdMap[sid] = uid;
        flatMilestones.push({ task: t, shortId: sid, parentGroupShortId });
      } else {
        const sid = shortId('task', taskIndex++);
        const uid = String(t.uniqueID || t.id || t.name);
        uidToTaskShortId.set(uid, sid);
        taskIdMap[sid] = t.name || 'Unnamed Task';
        taskRealIdMap[sid] = uid;
        flatTasks.push({ task: t, shortId: sid, parentGroupShortId });
        // If a non-summary task has children, recurse
        if (t.children) classifyTasks(t.children, parentGroupShortId);
      }
    }
  }

  classifyTasks(data.tasks, null);

  // Assign resource short IDs
  for (const r of data.resources || []) {
    const sid = shortId('res', resourceIndex++);
    const uid = String(r.uniqueID || r.id || r.name);
    uidToResShortId.set(uid, sid);
    resourceIdMap[sid] = r.name!;
    resourceRealIdMap[sid] = uid;
  }

  // Helper to find short ID for any entity by uniqueID
  function findShortId(uid: string | number): string | null {
    const key = String(uid);
    return uidToTaskShortId.get(key)
      || uidToMsShortId.get(key)
      || uidToGroupShortId.get(key)
      || null;
  }

  // Helper to parse duration string to hours
  function durationToHours(durStr: string | number | undefined | null, durMinutes: number | undefined | null): number {
    if (durMinutes != null) return durMinutes / 60;
    if (typeof durStr === 'number' && Number.isFinite(durStr)) return durStr / 60;
    if (!durStr) return 0;
    if (typeof durStr !== 'string') return 0;
    const m = durStr.match(/^([\d.]+)\s*(d|h|m|w)/i);
    if (!m) return 0;
    const val = parseFloat(m[1]);
    switch (m[2].toLowerCase()) {
      case 'm': return val / 60;
      case 'h': return val;
      case 'd': return val * 8;
      case 'w': return val * 40;
      default: return val;
    }
  }

  function optionalDurationToHours(durStr: string | undefined | null, durMinutes: number | undefined | null): number | undefined {
    if (durMinutes == null && (durStr == null || durStr === '')) return undefined;
    return durationToHours(durStr, durMinutes);
  }

  // Convert tasks
  let totalEffortHours = 0;
  let completedCount = 0;
  let inProgressCount = 0;

  function buildTaskSummary(item: FlatTask): TaskSummary {
    const t = item.task;
    const durationHours = durationToHours(t.duration, t.durationMinutes);
    const effortHours = durationToHours(t.work, t.workMinutes);
    const baselineDurationHours = optionalDurationToHours(t.baselineDuration, t.baselineDurationMinutes);
    const actualDurationHours = optionalDurationToHours(t.actualDuration, t.actualDurationMinutes);
    const remainingDurationHours = optionalDurationToHours(t.remainingDuration, t.remainingDurationMinutes);
    const actualWorkHours = optionalDurationToHours(t.actualWork, t.actualWorkMinutes);
    const remainingWorkHours = optionalDurationToHours(t.remainingWork, t.remainingWorkMinutes);
    totalEffortHours += effortHours;

    const pctComplete = t.percentComplete || 0;
    let status: TaskSummary['status'] = 'Not Started';
    if (pctComplete >= 100 || t.activityStatus === 'completed') {
      status = 'Completed';
      completedCount++;
    } else if (pctComplete > 0 || t.activityStatus === 'inProgress') {
      status = 'In Progress';
      inProgressCount++;
    }

    const resourceNames: string[] = [];
    if (t.resourceAssignments) {
      for (const a of t.resourceAssignments) {
        if (a.resourceName) resourceNames.push(a.resourceName);
      }
    }
    const assignments: TaskAssignmentSummary[] = (t.resourceAssignments || []).map(a => {
      const resourceShortId = a.resourceUniqueID != null
        ? uidToResShortId.get(String(a.resourceUniqueID))
        : undefined;
      return {
        resourceName: a.resourceName || 'Unnamed Resource',
        resourceShortId,
        units: a.units != null ? Math.round(a.units * 100) / 100 : undefined,
        workHours: optionalDurationToHours(a.work, a.workMinutes),
        actualWorkHours: optionalDurationToHours(a.actualWork, a.actualWorkMinutes),
        remainingWorkHours: optionalDurationToHours(a.remainingWork, a.remainingWorkMinutes),
        start: a.start || undefined,
        finish: a.finish || undefined,
        actualStart: a.actualStart || undefined,
        actualFinish: a.actualFinish || undefined,
      };
    });

    // Gather dependencies from predecessors
    const dependencies: string[] = [];
    if (t.predecessors) {
      for (const p of t.predecessors) {
        if (p.taskName) dependencies.push(p.taskName);
      }
    }

    // Find group for this task
    const groupShortId = item.parentGroupShortId || undefined;
    const groupName = groupShortId ? (groupIdMap[groupShortId] || '') : '';

    return {
      name: t.name || 'Unnamed',
      shortId: item.shortId,
      groupName,
      groupShortId,
      taskType: t.isMilestone ? 'milestone' : 'task',
      calendarName: t.calendarName || undefined,
      importedTaskType: t.taskType || undefined,
      importedActivityType: t.activityType || undefined,
      effortDriven: typeof t.effortDriven === 'boolean' ? t.effortDriven : undefined,
      active: typeof t.active === 'boolean' ? t.active : undefined,
      effortHours: Math.round(effortHours * 100) / 100,
      durationHours: Math.round(durationHours * 100) / 100,
      baselineStart: t.baselineStart || undefined,
      baselineFinish: t.baselineFinish || undefined,
      baselineDurationHours: baselineDurationHours != null ? Math.round(baselineDurationHours * 100) / 100 : undefined,
      actualStart: t.actualStart || undefined,
      actualFinish: t.actualFinish || undefined,
      actualDurationHours: actualDurationHours != null ? Math.round(actualDurationHours * 100) / 100 : undefined,
      remainingDurationHours: remainingDurationHours != null ? Math.round(remainingDurationHours * 100) / 100 : undefined,
      actualWorkHours: actualWorkHours != null ? Math.round(actualWorkHours * 100) / 100 : undefined,
      remainingWorkHours: remainingWorkHours != null ? Math.round(remainingWorkHours * 100) / 100 : undefined,
      deadline: t.deadline || undefined,
      constraintType: t.constraintType || undefined,
      constraintDate: t.constraintDate || undefined,
      secondaryConstraintType: t.secondaryConstraintType || undefined,
      secondaryConstraintDate: t.secondaryConstraintDate || undefined,
      totalSlackHours: optionalDurationToHours(t.totalSlack, t.totalSlackMinutes),
      freeSlackHours: optionalDurationToHours(t.freeSlack, t.freeSlackMinutes),
      startSlackHours: optionalDurationToHours(t.startSlack, t.startSlackMinutes),
      finishSlackHours: optionalDurationToHours(t.finishSlack, t.finishSlackMinutes),
      floatPath: t.floatPath ?? undefined,
      floatPathOrder: t.floatPathOrder ?? undefined,
      longestPath: typeof t.longestPath === 'boolean' ? t.longestPath : undefined,
      scheduledStart: t.start || undefined,
      scheduledFinish: t.finish || undefined,
      status,
      percentComplete: pctComplete,
      isOnCriticalPath: !!t.critical,
      subtaskCount: 0,
      dependencies,
      resourceNames,
      assignments,
    };
  }

  const taskSummaries = flatTasks.map(buildTaskSummary);
  const milestoneSummaries = flatMilestones.map(buildTaskSummary);

  // Convert groups from summary tasks
  const groupSummaries: GroupSummary[] = flatGroups.map(item => {
    const t = item.task;
    const aggregate = summarizeDescendantTasks(t.children || []);

    return {
      name: t.name || 'Unnamed Group',
      shortId: item.shortId,
      parentGroupName: item.parentGroupShortId ? (groupIdMap[item.parentGroupShortId] || undefined) : undefined,
      parentShortId: item.parentGroupShortId || undefined,
      numberOfTasks: aggregate.taskCount,
      numberOfMeetings: 0,
      plannedWorkTasks: Math.round(aggregate.totalEffortHours * 100) / 100,
      plannedWorkMeetings: 0,
      tasksCompletedCount: aggregate.tasksCompletedCount,
      tasksInProgressCount: aggregate.tasksInProgressCount,
      tasksNotYetStartedCount: aggregate.tasksNotStartedCount,
      meetingsCompletedCount: 0,
      meetingsInProgressCount: 0,
      meetingsNotYetStartedCount: 0,
      scheduledStart: t.start || undefined,
      scheduledEnd: t.finish || undefined,
    };
  });

  // Convert resources
  const resourceSummaries: ResourceSummary[] = (data.resources || []).map(r => {
    const uid = String(r.uniqueID || r.id || r.name);
    const resourceCalendar = resolveResourceCalendar(r);
    return {
      name: r.name || 'Unnamed',
      shortId: uidToResShortId.get(uid),
      type: r.type || 'work',
      resourceLevellingEnabled: typeof r.canLevel === 'boolean' ? r.canLevel : false,
      maxActiveTasks: 'unlimited' as const,
      maxUnitsPercent: normalizeMaxUnitsPercent(r.maxUnits),
      calendar: resourceCalendar ? getCanonicalCalendarName(resourceCalendar) : (r.calendarName || 'Standard'),
      calendarHoursPerDay: resourceCalendar ? getCalendarHoursPerDay(resourceCalendar) : undefined,
      holidays: resourceCalendar ? getCalendarHolidays(resourceCalendar) : undefined,
      workForTasks: [],
      workOverall: null,
    };
  });

  // Convert links from predecessors
  const linkSummaries: LinkSummary[] = [];
  function collectLinks(tasks: ImportedTask[] | undefined): void {
    if (!tasks) return;
    for (const t of tasks) {
      if (t.predecessors) {
        const toUid = String(t.uniqueID || t.id || t.name);
        const toSid = findShortId(toUid);
        for (const p of t.predecessors) {
          const fromUid = String(p.taskUniqueID || p.taskID || '');
          const fromSid = findShortId(fromUid);
          if (!fromSid || !toSid) continue;

          const linkType = mapLinkType(p.type);
          const link: LinkSummary = {
            fromTaskName: p.taskName || 'Unknown',
            fromShortId: fromSid,
            toTaskName: t.name || 'Unknown',
            toShortId: toSid,
            linkType,
          };

          if (p.lagMinutes && p.lagMinutes !== 0) {
            link.lag = {
              days: Math.round((p.lagMinutes / 480) * 100) / 100,
              units: 'wd',
            };
          }

          linkSummaries.push(link);
        }
      }
      if (t.children) collectLinks(t.children);
    }
  }
  collectLinks(data.tasks);

  const criticalPaths = buildCriticalPaths([...taskSummaries, ...milestoneSummaries], linkSummaries, groupSummaries);

  function mapLinkType(type: string | undefined): LinkSummary['linkType'] {
    if (!type) return 'FS';
    const t = type.toLowerCase().replace(/[_\s]/g, '');
    if (t === 'finishstart' || t === 'fs') return 'FS';
    if (t === 'startstart' || t === 'ss') return 'SS';
    if (t === 'finishfinish' || t === 'ff') return 'FF';
    if (t === 'startfinish' || t === 'sf') return 'SF';
    return 'FS';
  }

  function summarizeDescendantTasks(tasks: ImportedTask[]): DescendantAggregate {
    const aggregate: DescendantAggregate = {
      taskCount: 0,
      totalEffortHours: 0,
      tasksCompletedCount: 0,
      tasksInProgressCount: 0,
      tasksNotStartedCount: 0,
    };

    for (const task of tasks || []) {
      if (task.isSummary && task.children && task.children.length > 0) {
        const childAggregate = summarizeDescendantTasks(task.children);
        aggregate.taskCount += childAggregate.taskCount;
        aggregate.totalEffortHours += childAggregate.totalEffortHours;
        aggregate.tasksCompletedCount += childAggregate.tasksCompletedCount;
        aggregate.tasksInProgressCount += childAggregate.tasksInProgressCount;
        aggregate.tasksNotStartedCount += childAggregate.tasksNotStartedCount;
        continue;
      }

      if (task.isMilestone) {
        continue;
      }

      aggregate.taskCount++;
      aggregate.totalEffortHours += durationToHours(task.work, task.workMinutes);

      const pct = task.percentComplete || 0;
      if (pct >= 100 || task.activityStatus === 'completed') {
        aggregate.tasksCompletedCount++;
      } else if (pct > 0 || task.activityStatus === 'inProgress') {
        aggregate.tasksInProgressCount++;
      } else {
        aggregate.tasksNotStartedCount++;
      }
    }

    return aggregate;
  }

  function formatLocalDate(date: Date): string {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return year + '-' + month + '-' + day;
  }

  function getBrowserLocales(): string[] | undefined {
    if (typeof navigator === 'undefined') return undefined;

    const locales = Array.isArray(navigator.languages) && navigator.languages.length
      ? navigator.languages
      : navigator.language
        ? [navigator.language]
        : [];
    const cleaned = locales
      .map(value => String(value || '').trim())
      .filter(Boolean);
    return cleaned.length ? cleaned : undefined;
  }

  function getBrowserTimezone(): string | undefined {
    try {
      if (typeof Intl === 'undefined' || typeof Intl.DateTimeFormat !== 'function') return undefined;
      return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
    } catch {
      return undefined;
    }
  }

  const planSummary = {
    planName: data.projectName || 'Untitled Project',
    sourceFormat: data.sourceFormat || undefined,
    planTimezone: getBrowserTimezone(),
    userLocales: getBrowserLocales(),
    schedulingStartDate: data.startDate || '',
    todaysDate: formatLocalDate(new Date()),
    statusDate: data.statusDate || data.startDate || undefined,
    plannedStartDate: data.startDate || '',
    plannedEndDate: data.finishDate || '',
    mustFinishBy: data.mustFinishBy || undefined,
    scheduleFrom: data.scheduleFrom || undefined,
    defaultTaskType: data.defaultTaskType || undefined,
    defaultDurationUnits: data.defaultDurationUnits || undefined,
    schedulingProgressMode: data.schedulingProgressMode || undefined,
    lagCalendarMode: data.lagCalendarMode || undefined,
    calculateMultipleFloatPaths: typeof data.calculateMultipleFloatPaths === 'boolean' ? data.calculateMultipleFloatPaths : undefined,
    calculateMultipleFloatPathsUsingTotalFloat: typeof data.calculateMultipleFloatPathsUsingTotalFloat === 'boolean' ? data.calculateMultipleFloatPathsUsingTotalFloat : undefined,
    multipleCriticalPaths: typeof data.multipleCriticalPaths === 'boolean' ? data.multipleCriticalPaths : undefined,
    criticalActivityType: data.criticalActivityType || undefined,
    totalSlackCalculationType: data.totalSlackCalculationType || undefined,
    defaultHoursPerDay: data.minutesPerDay ? (data.minutesPerDay as number) / 60 : undefined,
    defaultHoursPerWeek: data.minutesPerWeek ? data.minutesPerWeek / 60 : undefined,
    defaultHoursPerMonth: data.minutesPerMonth ? data.minutesPerMonth / 60 : undefined,
    groups: groupSummaries,
    tasks: taskSummaries,
    meetings: [] as MeetingSummary[],
    milestones: milestoneSummaries,
    resources: resourceSummaries,
    links: linkSummaries,
    criticalPaths,
    metrics: {
      taskCount: flatTasks.length,
      totalEffortHours: Math.round(totalEffortHours * 100) / 100,
      resourceCount: (data.resources || []).length,
      completedTaskCount: completedCount,
      inProgressTaskCount: inProgressCount,
      milestoneCount: flatMilestones.length,
      meetingCount: 0,
    },
  };

  return {
    planSummary,
    taskIdMap,
    groupIdMap,
    resourceIdMap,
    milestoneIdMap,
    meetingIdMap: {},
    taskRealIdMap,
    groupRealIdMap,
    resourceRealIdMap,
    milestoneRealIdMap,
    meetingRealIdMap: {},
  };

  function buildCriticalPaths(items: TaskSummary[], links: LinkSummary[], groups: GroupSummary[]): CriticalPathSummary[] {
    const vendorPaths = buildVendorCriticalPaths(items);
    if (vendorPaths.length > 0) {
      return vendorPaths;
    }

    const criticalItems = items.filter(item => item.shortId && item.isOnCriticalPath);
    if (!criticalItems.length) return [];

    const criticalIds = new Set(criticalItems.map(item => item.shortId));
    const outgoing = new Map<string, string[]>();
    const incomingCount = new Map<string, number>();
    const groupById = new Map<string, GroupSummary>((groups || []).filter(group => group && group.shortId).map(group => [group.shortId, group]));
    const itemById = new Map<string, TaskSummary>(criticalItems.map(item => [item.shortId, item]));
    const boundaryCache = new Map<string, string[]>();

    for (const item of criticalItems) {
      outgoing.set(item.shortId, []);
      incomingCount.set(item.shortId, 0);
    }

    for (const link of links) {
      if (!criticalIds.has(link.fromShortId) || !criticalIds.has(link.toShortId) || link.fromShortId === link.toShortId) continue;
      addEdge(link.fromShortId, link.toShortId);
    }

    for (const link of links) {
      const fromIsGroup = groupById.has(link.fromShortId);
      const toIsGroup = groupById.has(link.toShortId);
      if (!fromIsGroup && !toIsGroup) continue;

      const fromIds = resolveLinkEndpoint(link.fromShortId, endpointEvent(link.linkType, 'from'));
      const toIds = resolveLinkEndpoint(link.toShortId, endpointEvent(link.linkType, 'to'));

      fromIds.forEach(fromId => {
        toIds.forEach(toId => {
          if (!criticalIds.has(fromId) || !criticalIds.has(toId) || fromId === toId) return;
          addEdge(fromId, toId);
        });
      });
    }

    const starts = criticalItems
      .filter(item => (incomingCount.get(item.shortId) || 0) === 0)
      .sort((a, b) => (a.scheduledStart || '').localeCompare(b.scheduledStart || ''))
      .map(item => item.shortId);

    const allStarts = starts.length
      ? starts
      : criticalItems
        .sort((a, b) => (a.scheduledStart || '').localeCompare(b.scheduledStart || ''))
        .map(item => item.shortId);

    const undirected = new Map<string, Set<string>>();
    for (const item of criticalItems) {
      undirected.set(item.shortId, new Set());
    }

    outgoing.forEach((nextIds, fromId) => {
      nextIds.forEach(toId => {
        undirected.get(fromId)!.add(toId);
        undirected.get(toId)!.add(fromId);
      });
    });

    const visitedComponents = new Set<string>();
    const components: string[][] = [];

    for (const startId of allStarts) {
      if (visitedComponents.has(startId)) continue;
      const stack = [startId];
      const componentIds: string[] = [];

      while (stack.length) {
        const currentId = stack.pop()!;
        if (visitedComponents.has(currentId)) continue;
        visitedComponents.add(currentId);
        componentIds.push(currentId);
        (undirected.get(currentId) || new Set()).forEach(nextId => {
          if (!visitedComponents.has(nextId)) {
            stack.push(nextId);
          }
        });
      }

      if (componentIds.length) {
        components.push(componentIds);
      }
    }

    for (const item of criticalItems) {
      if (!visitedComponents.has(item.shortId)) {
        components.push([item.shortId]);
      }
    }

    return components
      .map((componentIds, index) => {
        const componentSet = new Set(componentIds);
        const localIncoming = new Map<string, number>(componentIds.map(id => [id, 0]));
        componentIds.forEach(id => {
          (outgoing.get(id) || []).forEach(nextId => {
            if (componentSet.has(nextId)) {
              localIncoming.set(nextId, (localIncoming.get(nextId) || 0) + 1);
            }
          });
        });

        const orderedStarts = componentIds
          .filter(id => (localIncoming.get(id) || 0) === 0)
          .sort(compareByStartDate);
        const seedIds = orderedStarts.length ? orderedStarts : [...componentIds].sort(compareByStartDate);
        const orderedIds: string[] = [];
        const visitedInComponent = new Set<string>();

        function appendChain(currentId: string): void {
          if (visitedInComponent.has(currentId)) return;
          visitedInComponent.add(currentId);
          orderedIds.push(currentId);
          const nextIds = (outgoing.get(currentId) || [])
            .filter(nextId => componentSet.has(nextId) && !visitedInComponent.has(nextId))
            .sort(compareByStartDate);
          nextIds.forEach(appendChain);
        }

        seedIds.forEach(appendChain);
        componentIds
          .filter(id => !visitedInComponent.has(id))
          .sort(compareByStartDate)
          .forEach(appendChain);

        return {
          name: `Derived Critical Path ${index + 1}`,
          ids: orderedIds,
          _sortKey: itemById.get(orderedIds[0])?.scheduledStart || '',
        };
      })
      .sort((a, b) => a._sortKey.localeCompare(b._sortKey))
      .map(({ name, ids }) => ({ name, ids }));

    function compareByStartDate(a: string, b: string): number {
      return (itemById.get(a)?.scheduledStart || '').localeCompare(itemById.get(b)?.scheduledStart || '');
    }

    function addEdge(fromId: string, toId: string): void {
      const nextIds = outgoing.get(fromId);
      if (!nextIds || nextIds.includes(toId)) return;
      nextIds.push(toId);
      incomingCount.set(toId, (incomingCount.get(toId) || 0) + 1);
    }

    function endpointEvent(linkType: string, side: 'from' | 'to'): 'start' | 'finish' {
      const normalized = typeof linkType === 'string' ? linkType.toUpperCase() : 'FS';
      if (side === 'from') {
        return normalized.charAt(0) === 'S' ? 'start' : 'finish';
      }
      return normalized.charAt(1) === 'F' ? 'finish' : 'start';
    }

    function resolveLinkEndpoint(shortId: string, eventType: 'start' | 'finish'): string[] {
      if (!shortId) return [];
      if (criticalIds.has(shortId)) return [shortId];
      if (!groupById.has(shortId)) return [];
      return getGroupBoundaryIds(shortId, eventType);
    }

    function getGroupBoundaryIds(groupId: string, eventType: 'start' | 'finish'): string[] {
      const cacheKey = groupId + ':' + eventType;
      if (boundaryCache.has(cacheKey)) {
        return boundaryCache.get(cacheKey)!;
      }

      const descendantIds = criticalItems
        .filter(item => isInGroupHierarchy(item, groupId))
        .map(item => item.shortId);

      if (!descendantIds.length) {
        boundaryCache.set(cacheKey, []);
        return [];
      }

      const descendantSet = new Set(descendantIds);
      const boundaryIds = descendantIds.filter(itemId => {
        if (eventType === 'start') {
          return !descendantIds.some(otherId => (outgoing.get(otherId) || []).includes(itemId) && descendantSet.has(otherId));
        }
        return !(outgoing.get(itemId) || []).some(nextId => descendantSet.has(nextId));
      });

      const resolvedIds = (boundaryIds.length ? boundaryIds : descendantIds)
        .sort((leftId, rightId) => {
          const leftItem = itemById.get(leftId);
          const rightItem = itemById.get(rightId);
          const leftDate = eventType === 'start'
            ? (leftItem?.scheduledStart || leftItem?.scheduledFinish || '')
            : (leftItem?.scheduledFinish || leftItem?.scheduledStart || '');
          const rightDate = eventType === 'start'
            ? (rightItem?.scheduledStart || rightItem?.scheduledFinish || '')
            : (rightItem?.scheduledFinish || rightItem?.scheduledStart || '');
          return leftDate.localeCompare(rightDate);
        });

      boundaryCache.set(cacheKey, resolvedIds);
      return resolvedIds;
    }

    function isInGroupHierarchy(item: TaskSummary, groupId: string): boolean {
      let currentGroupId: string | undefined = item && item.groupShortId;
      while (currentGroupId) {
        if (currentGroupId === groupId) {
          return true;
        }
        currentGroupId = groupById.get(currentGroupId)?.parentShortId;
      }
      return false;
    }
  }

  function buildVendorCriticalPaths(items: TaskSummary[]): CriticalPathSummary[] {
    const withFloatPath = items
      .filter(item => item.shortId && item.floatPath != null)
      .sort((a, b) => {
        if (a.floatPath !== b.floatPath) return a.floatPath! - b.floatPath!;
        if ((a.floatPathOrder ?? Number.MAX_SAFE_INTEGER) !== (b.floatPathOrder ?? Number.MAX_SAFE_INTEGER)) {
          return (a.floatPathOrder ?? Number.MAX_SAFE_INTEGER) - (b.floatPathOrder ?? Number.MAX_SAFE_INTEGER);
        }
        return (a.scheduledStart || '').localeCompare(b.scheduledStart || '');
      });

    if (!withFloatPath.length) return [];

    const byPath = new Map<number, TaskSummary[]>();
    for (const item of withFloatPath) {
      const bucket = byPath.get(item.floatPath!) || [];
      bucket.push(item);
      byPath.set(item.floatPath!, bucket);
    }

    return [...byPath.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([pathNumber, pathItems]) => ({
        name: pathItems.some(item => item.longestPath) ? `Imported Longest Path ${pathNumber}` : `Imported Float Path ${pathNumber}`,
        ids: pathItems.map(item => item.shortId),
      }));
  }
}
